import { isDefined } from '@sgme/fp';
import type { ReactElement } from 'react';
import Toast from 'react-bootstrap/Toast';
import { useDispatch } from 'react-redux';

import { GrowlAlgoOrder } from '@/App/Growl/products/GrowlAlgoOrder.tsx';
import { GrowlCashForward } from '@/App/Growl/products/GrowlCashForward.tsx';
import { GrowlCashSpot } from '@/App/Growl/products/GrowlCashSpot.tsx';
import { GrowlFlexiForward } from '@/App/Growl/products/GrowlFlexiForward.tsx';
import { GrowlForwardAccumulator } from '@/App/Growl/products/GrowlForwardAccumulator.tsx';
import { GrowlSwap } from '@/App/Growl/products/GrowlSwap/index.tsx';
import { GrowlTargetAccumulator } from '@/App/Growl/products/GrowlTargetAccumulator.tsx';
import { GrowlOptionVanilla } from '@/App/Growl/products/option/GrowlOptionVanilla.tsx';
import { GrowlCatchAll } from '@/components/fatality/GrowlCatchAll.tsx';
import { GrowlSlice } from '@/store/growl/slice.ts';
import type { Growl as GrowlType } from '@/store/growl/state/index.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';

type GrowlProps = {
  growlId: string;
  growlItem: GrowlType;
};

const Growl = (props: GrowlProps) => {
  const { growlId, growlItem } = props;

  const dispatch = useDispatch();
  const { autoCloseGrowlAfter } = useAppSelector(getUserPreferences);

  const { options: { closeable = true, autoCloseAfter = autoCloseGrowlAfter } = {} } = growlItem;

  const delay = isDefined(autoCloseAfter) ? autoCloseAfter : undefined;

  const handleClose = () => {
    if (!closeable) {
      return;
    }
    dispatch(GrowlSlice.actions.delete({ growlId: growlId }));
  };

  let growlRendererImpl: ReactElement | null = null;

  switch (growlItem.type) {
    case 'algoCommandErrorFeedback':
      growlRendererImpl = <GrowlAlgoOrder growlItem={growlItem.data} />;
      break;
    case 'cashDealExecution': {
      if (growlItem.data.productName === 'FxSpot') {
        growlRendererImpl = <GrowlCashSpot growlItem={growlItem.data} />;
      } else {
        growlRendererImpl = <GrowlCashForward growlItem={growlItem.data} />;
      }
      break;
    }
    case 'swapDealExecution': {
      growlRendererImpl = <GrowlSwap growlItem={growlItem.data} />;
      break;
    }
    case 'optionDealExecution': {
      growlRendererImpl = <GrowlOptionVanilla growlItem={growlItem.data} />;
      break;
    }
    case 'flexiForwardDealExecution': {
      growlRendererImpl = <GrowlFlexiForward growlItem={growlItem.data} />;
      break;
    }
    case 'targetAccumulatorDealExecution': {
      growlRendererImpl = <GrowlTargetAccumulator growlItem={growlItem.data} />;
      break;
    }
    case 'forwardAccumulatorDealExecution': {
      growlRendererImpl = <GrowlForwardAccumulator growlItem={growlItem.data} />;
      break;
    }
  }

  const productName = 'productName' in growlItem.data ? growlItem.data.productName : growlItem.type;

  return (
    <>
      {isDefined(growlItem) && (
        <Toast
          delay={delay}
          autohide={isDefined(delay)}
          onClose={handleClose}
          style={{ width: 380 }}
          data-e2e="growl"
          data-e2e-status={'status' in growlItem.data ? growlItem.data.status : undefined}
        >
          <GrowlCatchAll growlId={growlId} growlType={growlItem.type} productName={productName}>
            {growlRendererImpl}
          </GrowlCatchAll>
        </Toast>
      )}
    </>
  );
};

export default Growl;
